/**
 * Modal de Novedades de AdvocatesPro (ADV-3243): anuncia el reporte descargable
 * de Atribución y los nuevos tipos de puntaje en Recompensas. Tercera pieza de
 * la familia de "Modales Especiales" compactos (la anterior:
 * `advproAmbassadorWebinar.constants.ts`). El mismo modal existe en la v3
 * (`web/src/lib/novedades/constants.ts` de advocatesegc); si cambia la fecha o
 * el copy, hay que tocar los dos repos.
 *
 * Lo ven los admins: las dos features anunciadas (exportar Atribución y
 * personalizar puntos por campaña) son pantallas de administrador.
 *
 * Comportamiento (solo admins, y solo en empresas no excluidas — ver
 * `excludedCompanies.constants.ts`):
 *  - Link "No volver a mostrar" → ÚNICA forma de descartarlo definitivamente.
 *    Marca permanente en localStorage POR USUARIO.
 *  - Aparece **una vez cada 7 días por persona** (ADV-3347, regla de toda la
 *    familia: ver `specialModalFrequency.constants.ts`). "Entendido",
 *    "Cerrar", X o clic en el fondo solo cierran la vista: la ventana ya se
 *    abrió al mostrarlo.
 *  - Ventana: desde el deploy hasta NOVEDADES_END_DATE inclusive. Pasada la
 *    fecha se apaga solo, sin deploy.
 *
 * Hasta ADV-3347 acá NO había snooze, igual que en el modal del webinar del rol
 * Embajador: se insistía en cada carga durante la semana de vigencia, con el
 * link de opt-out como única salida. Fue la experiencia repetitiva que originó
 * el ticket. El descarte va por usuario y no por empresa, por el mismo motivo
 * de siempre: una marca por empresa se la apagaría a todos los admins a la vez.
 */

import { isExcludedFromSpecialModals } from 'constants/excludedCompanies.constants';
import {
  SpecialModalUserId,
  wasSpecialModalShownRecently
} from 'constants/specialModalFrequency.constants';

/**
 * Corte de visibilidad: 16-sep-2026 23:59:59 hora México (UTC-6, la TZ más al
 * oeste de la audiencia). Salió a producción el martes 9 de septiembre; una
 * semana visible, hasta el 16 inclusive.
 */
export const NOVEDADES_END_DATE = '2026-09-16T23:59:59-06:00';

/** Prefijo de la marca de descarte permanente (se le concatena el id del user). */
const DISMISSED_KEY_PREFIX = 'novedades_2026sep_dismissed';

/** Key de este modal para la marca de frecuencia de la familia. */
export const NOVEDADES_MODAL_KEY = 'novedades_2026sep';

type UserId = SpecialModalUserId;

const dismissedKey = (userId: UserId): string =>
  `${DISMISSED_KEY_PREFIX}:${userId}`;

/** True mientras la campaña siga vigente (nada que ver con el usuario). */
export const isNovedadesActive = (): boolean =>
  Date.now() < new Date(NOVEDADES_END_DATE).getTime();

/**
 * Decide si el modal debe mostrarse: campaña vigente + empresa no excluida +
 * admin que todavía no clickeó "No volver a mostrar" + ventana de frecuencia
 * vencida para esta persona.
 */
export const shouldShowNovedadesModal = (userId: UserId): boolean => {
  // Sin id no hay forma de recordar el descarte: mostrarlo significaría
  // reaparecer incluso después del opt-out.
  if (userId === undefined || userId === null || userId === '') return false;
  if (isExcludedFromSpecialModals()) return false;
  if (!isNovedadesActive()) return false;
  try {
    if (localStorage.getItem(dismissedKey(userId)) === 'true') return false;
  } catch {
    // localStorage inaccesible (modo privado restrictivo): mejor no mostrarlo
    // que mostrarlo en cada carga sin poder recordar el descarte.
    return false;
  }
  if (wasSpecialModalShownRecently(NOVEDADES_MODAL_KEY, userId)) return false;
  return true;
};

/** Descarta el modal para siempre para este admin ("No volver a mostrar"). */
export const dismissNovedadesModal = (userId: UserId): void => {
  try {
    localStorage.setItem(dismissedKey(userId), 'true');
  } catch {
    // Sin almacenamiento disponible no hay nada que persistir.
  }
};
